import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, ApiError } from '../../lib/api.js';
import {
  SearchCriteriaBar,
  SearchParametersForm,
  type SearchParametersDraft,
} from '../../components/SearchParametersForm.js';
import { MatchedLocations } from '../../components/MatchedLocations.js';
import { track } from '../../lib/telemetry.js';

/**
 * Script → locations: the constraints, then what we found.
 *
 * ONE TAB, TWO STATES, and the state is a fact rather than a wizard step: the
 * parameters screen shows while nobody has answered, and the results once
 * somebody has. A producer can go back — the constraints are not a gate they
 * pass through once, they are a thing they will change after seeing what came
 * back, which is the normal way this goes.
 *
 * SINCE 2026-10-30 THE CRITERIA ARE A BAR, NOT A PAGE. The answered criteria
 * read as one row above the results, and "Edit criteria" opens the full form
 * in place, between the bar and the results. While it is open its "Search
 * locations" is the page's one red button; the results below give theirs up.
 */
export function MatchingTab({
  projectId,
  projectTitle = 'This production',
  scriptVersionLabel = '',
  focusPlaceId = null,
}: {
  projectId: string;
  projectTitle?: string;
  scriptVersionLabel?: string;
  /** A place a link pointed at (`?place=`), passed to its card. */
  focusPlaceId?: number | null;
}) {
  const queryClient = useQueryClient();
  const [editingParameters, setEditingParameters] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const parameters = useQuery({
    queryKey: ['search-parameters', projectId],
    queryFn: () => api.searchParameters(projectId),
  });

  const matched = useQuery({
    queryKey: ['matched', projectId],
    queryFn: () => api.matchedLocations(projectId),
  });

  const suggestions = useQuery({
    queryKey: ['merge-suggestions', projectId],
    queryFn: () => api.mergeSuggestions(projectId),
  });

  const refresh = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['matched', projectId] }),
      queryClient.invalidateQueries({ queryKey: ['search-parameters', projectId] }),
    ]);
  };

  const fail = (e: unknown): void => {
    setError(e instanceof ApiError ? e.message : 'That could not be saved.');
  };

  // A refused candidate write re-reads the matched view: every write is
  // compare-and-swap on `candidateVersion`, and after a 409 the row kept the
  // stale version, so every retry was refused the same way until a reload.
  const failAndReread = async (e: unknown): Promise<void> => {
    fail(e);
    await queryClient.invalidateQueries({ queryKey: ['matched', projectId] });
  };

  const save = useMutation({
    mutationFn: (draft: Partial<SearchParametersDraft>) =>
      api.saveSearchParameters(projectId, draft),
    onSuccess: async () => {
      setError(null);
      setEditingParameters(false);
      await refresh();
    },
    onError: fail,
  });

  // Reject was drawn on every card and wired to nothing. Same verb as the
  // setup flow's Explore step uses, on the same candidate row.
  const reject = useMutation({
    mutationFn: ({ candidateId, version, reason }: { candidateId: string; version: number; reason: string }) =>
      api.setCandidateStatus(projectId, candidateId, version, 'REJECTED', reason),
    onSuccess: refresh,
    onError: failAndReread,
  });

  // A rejected place back among the candidates: the API's only way out of
  // REJECTED is the longlist, never straight onto the shortlist.
  const restore = useMutation({
    mutationFn: ({ candidateId, version }: { candidateId: string; version: number }) =>
      api.setCandidateStatus(projectId, candidateId, version, 'LONGLIST'),
    onSuccess: refresh,
    onError: failAndReread,
  });

  const shortlist = useMutation({
    mutationFn: ({ candidateId, version }: { candidateId: string; version: number }) =>
      api.setCandidateStatus(projectId, candidateId, version, 'SHORTLIST'),
    onSuccess: refresh,
    onError: failAndReread,
  });

  const merge = useMutation({
    mutationFn: (requirementIds: readonly string[]) =>
      api.mergeRequirements(projectId, requirementIds),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['merge-suggestions', projectId] });
      await refresh();
    },
    onError: fail,
  });

  const scouting = useMutation({
    mutationFn: ({ requirementIds, note }: { requirementIds: readonly string[]; note: string }) =>
      api.createRequest(projectId, {
        type: 'FIND_LOCATION',
        title: `Source ${String(requirementIds.length)} location${requirementIds.length === 1 ? '' : 's'}`,
        // What the script does not say — access, period accuracy, budget
        // ceilings. Omitted rather than sent empty, so a coordinator can tell
        // "nothing to add" from "they left the box alone".
        ...(note === '' ? {} : { body: note }),
        targets: requirementIds.map((id) => ({ targetType: 'REQUIREMENT', targetId: id })),
      }),
    onSuccess: refresh,
    onError: fail,
  });

  if (parameters.isPending || matched.isPending) {
    return (
      <p className="mm" data-testid="matching-loading" style={{ fontSize: 13 }}>
        Loading…
      </p>
    );
  }

  if (parameters.isError || matched.isError || !parameters.data || !matched.data) {
    return (
      <p data-testid="matching-error" style={{ fontSize: 13, color: 'var(--danger)' }}>
        Matching could not be loaded.
      </p>
    );
  }

  const answered = parameters.data.current.setAt !== null;
  const busy =
    save.isPending || shortlist.isPending || reject.isPending || restore.isPending || merge.isPending || scouting.isPending;

  // A link that points at one of the matched places (a conversation's "Show in
  // Locations") came to see that place, so the results show even before the
  // parameters were ever answered; "Edit requirements" still opens the form.
  const pointedAtMatched =
    focusPlaceId !== null && matched.data.locations.some((l) => l.sourceId === focusPlaceId);

  // Never answered, or explicitly reopened. "Never asked" and "asked and
  // skipped" are different states — the second must not keep reopening.
  const firstAsk = !answered && !pointedAtMatched;
  const formOpen = firstAsk || editingParameters;

  return (
    <div className="loc-page" data-testid="matching-tab">
      <div style={{ display: 'grid', gap: 0 }}>
        {firstAsk ? null : (
          <SearchCriteriaBar
            current={parameters.data.current}
            catalogue={parameters.data.catalogue}
            editing={editingParameters}
            onEdit={() => {
              setEditingParameters((v) => !v);
            }}
          />
        )}
        {formOpen ? (
          <div className="loc-criteria-panel" data-testid="search-criteria-panel">
            {error === null ? null : (
              <p style={{ fontSize: 13, color: 'var(--danger)', marginTop: 0 }} data-testid="matching-save-error">
                {error}
              </p>
            )}
            <SearchParametersForm
              current={parameters.data.current}
              catalogue={parameters.data.catalogue}
              recommended={parameters.data.recommended.countries}
              disclaimer={parameters.data.recommended.disclaimer}
              busy={busy}
              framed={!firstAsk}
              onSearch={(draft) => save.mutate(draft)}
              // Skip is a real answer: it saves nothing but the stamp, so the
              // producer is not asked again and matching runs on the script alone.
              onSkip={() => save.mutate({ countries: [] })}
              {...(firstAsk
                ? {}
                : {
                    onCancel: () => {
                      setEditingParameters(false);
                    },
                  })}
            />
          </div>
        ) : null}
      </div>

      {firstAsk ? null : (
        <>
          {error === null || formOpen ? null : (
            <p style={{ fontSize: 13, color: 'var(--danger)', margin: 0 }} data-testid="matching-action-error">
              {error}
            </p>
          )}
          <MatchedLocations
            view={matched.data}
            projectId={projectId}
            mergeSuggestions={suggestions.data ?? []}
            twinRequired={parameters.data.current.twinRequired}
            projectTitle={projectTitle}
            scriptVersionLabel={scriptVersionLabel}
            busy={busy}
            primaryAllowed={!formOpen}
            focusPlaceId={focusPlaceId}
            onShortlist={(candidateId, version) => shortlist.mutate({ candidateId, version })}
            onReject={(candidateId, version, reason) => reject.mutate({ candidateId, version, reason })}
        onRestore={(candidateId, version) => restore.mutate({ candidateId, version })}
            // `onAssignScenes` stays unwired ON PURPOSE: no API verb assigns a
            // candidate to scenes today — scene scope lives on the requirement —
            // so wiring the button would be a click that saves nothing. The row
            // offers it only when the prop is passed.
            onMerge={(ids) => merge.mutate(ids)}
            onRequestScouting={(requirementIds, note) => {
              scouting.mutate({ requirementIds, note });
            }}
            onPackPrinted={(count) => {
              track(projectId, 'location_pack_printed', { locations: count });
            }}
          />
        </>
      )}
    </div>
  );
}
